// Tipos centrais do motor. Nada aqui conhece o tema: cidades, bosses, vocações
// e guildas são apenas ids (strings) vindos dos dados.

export type PlayerId = number;
export type TerritoryId = string;
export type RegionId = string;
export type CardSymbol = string;
export type ColorId = string;

/** Vocações genéricas: o tema dá nome a cada uma. */
export type VocationId = 'defense' | 'range' | 'damage' | 'heal' | 'harmony';
export const VOCATIONS: readonly VocationId[] = ['defense', 'range', 'damage', 'heal', 'harmony'];

/** Efeitos de loot genéricos. O tema dá nome e arte a cada um. */
export type LootKind =
  | 'strike' // remove 2 tropas de um inimigo vizinho
  | 'barrage' // remove 1 tropa de até 3 inimigos vizinhos a um território seu
  | 'strength' // +1 no maior dado de ataque até o fim do turno
  | 'precision' // empates do atacante na próxima rolagem de ataque
  | 'blessing' // defensor vence tudo no próximo combate do território
  | 'wall' // bloqueia uma ligação até o início do próximo turno do dono
  | 'restore' // +3 tropas num território seu que foi atacado
  | 'ward' // território perde no máximo 1 tropa por rolagem
  | 'paralyze' // território inimigo não ataca nem se move no próximo turno do dono
  | 'luck' // reação: rerrola os próprios dados de uma rolagem
  | 'captain' // uma vez no turno, ataca um porto inimigo a partir de um porto seu
  | 'teleport' // move tropas entre dois territórios próprios
  | 'reinforce' // +3 tropas para posicionar
  | 'draw' // compra 2 cartas de território
  | 'steal'; // pega 1 carta de território de um vizinho
export const LOOT_KINDS: readonly LootKind[] = [
  'strike',
  'barrage',
  'strength',
  'precision',
  'blessing',
  'wall',
  'restore',
  'ward',
  'paralyze',
  'luck',
  'captain',
  'teleport',
  'reinforce',
  'draw',
  'steal',
];

/** Efeitos com duração, guardados em `state.effects`. */
export type EffectKind = 'strength' | 'precision' | 'captain' | 'blessing' | 'wall' | 'ward' | 'paralyze' | 'frozen' | 'flow' | 'flowGuard';

export type EffectExpiry =
  /** No fim do turno do jogador. */
  | { at: 'turnEnd'; player: PlayerId }
  /** No início do próximo turno do jogador. */
  | { at: 'turnStart'; player: PlayerId }
  /** No início da rodada indicada. */
  | { at: 'roundStart'; round: number }
  /** Só quando usado (ou se o território trocar de dono). */
  | { at: 'used' };

export interface Effect {
  id: number;
  kind: EffectKind;
  /** Quem criou (null para efeitos de boss). */
  owner: PlayerId | null;
  territory?: TerritoryId;
  /** Ligação afetada (Magic Wall). */
  link?: [TerritoryId, TerritoryId];
  expires: EffectExpiry;
}

export type BotDifficulty = 'easy' | 'normal';
export type SeatKind = 'human' | 'bot';

// ---------------------------------------------------------------- conteúdo

export interface RegionDef {
  id: RegionId;
  bonus: number;
}

export interface TerritoryDef {
  id: TerritoryId;
  region: RegionId;
  symbol: CardSymbol;
  /** Território com templo (a mecânica só vale com `ruleset.temples`). */
  temple?: boolean;
}

export interface LinkDef {
  a: TerritoryId;
  b: TerritoryId;
  /** Ligação por mar (rota de barco). */
  sea?: boolean;
  /** Tipo visual da ligação por terra (ponte, gelo…). Não muda a regra. */
  kind?: string;
}

export interface LairDef {
  region: RegionId;
  adjacent: TerritoryId[];
}

export interface MapDef {
  regions: RegionDef[];
  territories: TerritoryDef[];
  links: LinkDef[];
  lairs: LairDef[];
  /** Ordem das faces do dado de região (face 1 = índice 0). */
  regionDie: RegionId[];
  /** Ids das cartas curinga do baralho de território. */
  wildCards?: string[];
}

export const WILD_SYMBOL = 'wild';

export type RaidDef =
  | { type: 'damageAdjacent'; amount: number }
  | { type: 'quake'; adjacent: number; ring2: number }
  | { type: 'halveStrongest' }
  | { type: 'freeze'; amount: number; duration: number }
  | { type: 'damageAdjacentAndDiscard'; amount: number; discard: number };

export interface BossDef {
  id: string;
  hp: number;
  defenseDice: number;
  /** Rodadas até a raid. */
  timer: number;
  /** Cartas de loot compradas pelo último golpe (fica com 1). */
  lootDraw: number;
  raid: RaidDef;
}

/** Condição de vitória verificável no mapa. */
export type MissionCheck =
  | { type: 'regions'; regions: RegionId[]; /** + N regiões completas quaisquer, fora as listadas */ plusAny?: number }
  | { type: 'territories'; count: number; minTroops?: number }
  | { type: 'ports'; count: number }
  | { type: 'bossKills'; count: number }
  | { type: 'eliminate'; color: ColorId; fallback?: MissionCheck };

export type MissionDef = MissionCheck & {
  id: string;
  /** Missão trocada pelo `fallback` (alvo ausente, próprio ou eliminado por outro). */
  fallbackOf?: string;
};

export const FALLBACK_MISSION_ID = 'fallback-territories';

export interface GameContent {
  map: MapDef;
  bosses: BossDef[];
  missions: MissionDef[];
  /** Tipos de loot no baralho (cada um é copiado `ruleset.lootCopies` vezes). */
  loot: LootKind[];
}

// ---------------------------------------------------------------- regras

export interface Addons {
  sea: boolean;
  vocations: boolean;
  bosses: boolean;
}

export interface Ruleset {
  addons: Addons;
  /** Mecânica de templos (ainda não implementada; o mapa só mostra o ícone). */
  temples: boolean;
  minPlayers: number;
  maxPlayers: number;
  maxBots: number;
  /** Tropas iniciais totais por número de jogadores. */
  startingTroops: Record<number, number>;
  /** Tropas colocadas por vez na fase de posicionamento. */
  setupBatch: number;
  minReinforcements: number;
  territoriesPerReinforcement: number;
  tradeValues: number[];
  tradeIncrement: number;
  mandatoryTradeHand: number;
  maxAttackDice: number;
  maxDefenseDice: number;
  seaMaxDice: number;
  /** Faces do 1d6 do boss que fazem um boss surgir. */
  bossSpawnFaces: number[];
  strengthBonus: number;
  reinforceLootTroops: number;
  lootCopies: number;
  /** Máximo de cartas de loot na mão. */
  maxLoot: number;
  strikeDamage: number;
  barrageTargets: number;
  restoreTroops: number;
  drawCards: number;
  /** A cada N de vida tirada de um boss, o jogador compra 1 carta de território. */
  bossDamagePerCard: number;
  /** Cartas de loot para o primeiro jogador a dominar cada região. */
  firstRegionLoot: number;
  /** Prazo da janela de reação no modo online (o motor não mede tempo). */
  reactionTimeoutMs: number;
  serverSaveRound: number;
  scorePerTerritory: number;
  scorePerRegion: number;
  scorePerBossKill: number;
  fallbackMissionTerritories: number;
  /** Era escolhida (id opaco vindo do tema, só para exibição). */
  era: string | null;
  /** Trégua: até esta rodada (inclusive) ninguém ataca outro jogador, nem com poder ou loot. 0 = sem trégua. */
  truceRounds: number;
  /**
   * Cabeça a prêmio: no começo de cada rodada, quem tem mais territórios (sem empate)
   * fica marcado; a primeira conquista contra ele em cada turno rende 1 carta de território.
   */
  bounty: boolean;
  /**
   * Caveira: quem conquista este número de territórios num turno fica marcado até o
   * próximo turno dele; se perder um território nesse meio-tempo, descarta 1 carta e
   * 1 loot ao acaso e a marca cai. 0 = desligado.
   */
  skullConquests: number;
  /** Harmonia: máximo acumulado, mínimo para soltar a rajada e ganho máximo por turno. */
  harmonyMax: number;
  harmonyMin: number;
  harmonyPerTurn: number;
  /** Coordenação: +N no maior dado ao atacar um alvo já atacado no turno a partir de outro território. 0 = desligado. */
  coordinationBonus: number;
}

export type RulesetOverrides = Partial<Omit<Ruleset, 'addons'>> & { addons?: Partial<Addons> };

// ---------------------------------------------------------------- partida

export interface SeatConfig {
  name: string;
  color: ColorId;
  vocation: VocationId;
  kind: SeatKind;
  difficulty?: BotDifficulty;
}

export interface GameConfig {
  seats: SeatConfig[];
  seed: number;
  ruleset?: RulesetOverrides;
  content: GameContent;
}

/** Parte pública e imutável da configuração (sem a seed). */
export interface MatchInfo {
  content: GameContent;
  ruleset: Ruleset;
}

export type Phase = 'setup' | 'boss' | 'reinforce' | 'attack' | 'fortify' | 'over';

export interface PlayerPublic {
  id: PlayerId;
  name: string;
  color: ColorId;
  vocation: VocationId | null;
  kind: SeatKind;
  difficulty: BotDifficulty;
  alive: boolean;
  eliminatedBy: PlayerId | null;
  bossKills: number;
  /** Harmonia acumulada (vocação de harmonia); 0 para as outras. */
  harmony: number;
  /** Só presentes para o próprio jogador numa PlayerView. */
  cards?: TerritoryId[];
  loot?: LootKind[];
  mission?: MissionDef;
}

export interface PlayerState extends PlayerPublic {
  cards: TerritoryId[];
  loot: LootKind[];
  mission: MissionDef;
}

export interface TerritoryState {
  owner: PlayerId;
  troops: number;
}

export interface BossState {
  id: string;
  region: RegionId;
  hp: number;
  maxHp: number;
  timer: number;
  defenseDice: number;
  lootDraw: number;
  raid: RaidDef;
  /** Vida que cada jogador já tirou deste boss. */
  damage: Record<PlayerId, number>;
}

export interface TurnState {
  conquered: number;
  /** Tropas próprias perdidas no turno (para a cura). */
  lost: number;
  powerUsed: boolean;
  /** Tropas que já se moveram neste turno, por território de destino. */
  movedIn: Record<TerritoryId, number>;
  /** Origens de onde cada alvo já foi atacado neste turno (coordenação). */
  attackedFrom: Record<TerritoryId, TerritoryId[]>;
  /** A recompensa da cabeça a prêmio já foi paga neste turno. */
  bountyClaimed: boolean;
  /** Harmonia ganha neste turno (limite por turno). */
  harmonyGained: number;
}

export interface SetupState {
  remaining: number[];
  placedThisGo: number;
}

export type AttackRoute = 'land' | 'sea' | 'range' | 'captain';

export interface CombatSnapshot {
  target: 'territory' | 'boss';
  from: TerritoryId;
  to: TerritoryId | null;
  sea: boolean;
  attackRolls: number[];
  defenseRolls: number[];
  tieGoesTo: 'attacker' | 'defender';
  defenderWinsAll: boolean;
  notes: CombatNote[];
  /** Quem já decidiu sobre o Amuleto da Sorte nesta rolagem. */
  reacted: PlayerId[];
  /** Repetir até o fim depois de resolver (atacar até o fim). */
  untilEnd: boolean;
}

export type Pending =
  | { kind: 'conquest'; player: PlayerId; from: TerritoryId; to: TerritoryId; min: number; max: number; suggested: number }
  | { kind: 'lootChoice'; player: PlayerId; options: LootKind[] }
  | { kind: 'bossRegion'; player: PlayerId; regions: RegionId[] }
  /** Janela de reação: usar o Amuleto da Sorte nesta rolagem? */
  | { kind: 'reaction'; player: PlayerId; role: 'attacker' | 'defender'; combat: CombatSnapshot; timeoutMs: number }
  /** Mais cartas de loot que o limite: jogar ou descartar. */
  | { kind: 'lootOverflow'; player: PlayerId }
  | { kind: 'heal'; player: PlayerId }
  /** Na PlayerView de quem não decide: há uma decisão em segredo em andamento. */
  | { kind: 'hidden'; player: -1 };

export type CombatNote =
  | 'coordination'
  | 'flow'
  | 'flowGuard'
  | 'seaLimit'
  | 'captain'
  | 'strength'
  | 'knight'
  | 'knightUsed'
  | 'blessing'
  | 'luck'
  | 'luckDefender'
  | 'precision'
  | 'ward'
  | 'range'
  | 'captain';

export interface ScoreLine {
  player: PlayerId;
  territories: number;
  regions: number;
  bossKills: number;
  troops: number;
  score: number;
}

export interface GameResult {
  reason: 'mission' | 'lastStanding' | 'serverSave';
  winner: PlayerId;
  scores: ScoreLine[];
}

/** Estado completo. Só existe no navegador (modo local) ou no servidor. */
export interface GameState {
  seed: number;
  rng: number;
  info: MatchInfo;
  phase: Phase;
  round: number;
  current: PlayerId;
  setup: SetupState | null;
  reinforcements: number;
  territories: Record<TerritoryId, TerritoryState>;
  players: PlayerState[];
  deck: TerritoryId[];
  discard: TerritoryId[];
  tradeCount: number;
  lootDeck: LootKind[];
  lootDiscard: LootKind[];
  boss: BossState | null;
  bossDeck: string[];
  bossDiscard: string[];
  /** Efeitos com duração (loot e raids). */
  effects: Effect[];
  effectSeq: number;
  /** Territórios de cada jogador atacados desde o fim do último turno dele. */
  attacked: Record<PlayerId, TerritoryId[]>;
  /** Primeiro jogador a dominar cada região (bônus de loot uma vez por partida). */
  regionFirst: Record<RegionId, PlayerId>;
  /** Jogadores com a defesa (Knight) já usada no turno atual. */
  defenseUsed: PlayerId[];
  /** Cabeça a prêmio da rodada (regra de era). */
  bounty: PlayerId | null;
  /** Jogadores marcados com a caveira (regra de era). */
  skulls: PlayerId[];
  turn: TurnState;
  pending: Pending | null;
  eliminationOrder: PlayerId[];
  result: GameResult | null;
  /** Número de ações aplicadas. */
  seq: number;
  /** Eventos gerados pela última ação (para log e animação). */
  events: GameEvent[];
  /** Quem fez a última ação e se ela usou o RNG (bloqueia desfazer). */
  lastAction: { player: PlayerId; random: boolean } | null;
}

/** O que um jogador pode ver. Sem seed, sem baralhos, sem missões e cartas alheias. */
export interface PlayerView {
  me: PlayerId | null;
  info: MatchInfo;
  phase: Phase;
  round: number;
  current: PlayerId;
  setup: SetupState | null;
  reinforcements: number;
  territories: Record<TerritoryId, TerritoryState>;
  players: Array<PlayerPublic & { cardCount: number; lootCount: number }>;
  deckCount: number;
  tradeCount: number;
  boss: BossState | null;
  effects: Effect[];
  attacked: Record<PlayerId, TerritoryId[]>;
  regionFirst: Record<RegionId, PlayerId>;
  defenseUsed: PlayerId[];
  bounty: PlayerId | null;
  skulls: PlayerId[];
  turn: TurnState;
  pending: Pending | null;
  eliminationOrder: PlayerId[];
  result: GameResult | null;
  seq: number;
  events: GameEvent[];
}

/** Campos comuns a GameState e PlayerView, suficientes para calcular ações legais. */
export type RulesView = Pick<
  GameState,
  | 'info'
  | 'phase'
  | 'round'
  | 'current'
  | 'setup'
  | 'reinforcements'
  | 'territories'
  | 'tradeCount'
  | 'boss'
  | 'effects'
  | 'attacked'
  | 'regionFirst'
  | 'bounty'
  | 'skulls'
  | 'turn'
  | 'pending'
  | 'result'
> & { players: PlayerPublic[] };

// ---------------------------------------------------------------- ações

export type Action =
  | { type: 'place'; player: PlayerId; territory: TerritoryId; count: number }
  | { type: 'autoSetup'; player: PlayerId }
  | { type: 'trade'; player: PlayerId; cards: TerritoryId[] }
  | { type: 'endPhase'; player: PlayerId }
  | { type: 'attack'; player: PlayerId; from: TerritoryId; to: TerritoryId; untilEnd?: boolean }
  | { type: 'harmony'; player: PlayerId }
  | { type: 'attackBoss'; player: PlayerId; from: TerritoryId }
  | { type: 'moveIn'; player: PlayerId; count: number }
  | { type: 'fortify'; player: PlayerId; from: TerritoryId; to: TerritoryId; count: number }
  | { type: 'power'; player: PlayerId; target: TerritoryId }
  | { type: 'heal'; player: PlayerId; territory: TerritoryId }
  | {
      type: 'playLoot';
      player: PlayerId;
      kind: LootKind;
      territory?: TerritoryId;
      from?: TerritoryId;
      to?: TerritoryId;
      count?: number;
      /** Alvos da Great Fireball. */
      targets?: TerritoryId[];
      /** Jogador alvo do Saque. */
      victim?: PlayerId;
    }
  | { type: 'discardLoot'; player: PlayerId; kind: LootKind }
  | { type: 'chooseLoot'; player: PlayerId; kind: LootKind }
  | { type: 'chooseBossRegion'; player: PlayerId; region: RegionId }
  /** Resposta à janela de reação do Amuleto da Sorte. */
  | { type: 'react'; player: PlayerId; use: boolean };

export type ActionType = Action['type'];

// ---------------------------------------------------------------- eventos

export type GameEvent =
  | { type: 'setupPlaced'; player: PlayerId; territory: TerritoryId; count: number }
  | { type: 'placed'; player: PlayerId; territory: TerritoryId; count: number }
  | { type: 'setupDone' }
  | { type: 'roundStart'; round: number }
  | { type: 'turnStart'; player: PlayerId; round: number; reinforcements: number }
  | { type: 'phase'; player: PlayerId; phase: Phase }
  | { type: 'trade'; player: PlayerId; cards: TerritoryId[]; troops: number }
  | {
      type: 'combat';
      attacker: PlayerId;
      defender: PlayerId;
      from: TerritoryId;
      to: TerritoryId;
      attackRolls: number[];
      defenseRolls: number[];
      attackerLoss: number;
      defenderLoss: number;
      sea: boolean;
      notes: CombatNote[];
    }
  | {
      type: 'combatPreview';
      attacker: PlayerId;
      from: TerritoryId;
      to: TerritoryId | null;
      attackRolls: number[];
      defenseRolls: number[];
      /** Quem está decidindo sobre o amuleto (só essa pessoa vê o evento). */
      reactor: PlayerId;
    }
  | { type: 'conquest'; player: PlayerId; from: TerritoryId; to: TerritoryId; defender: PlayerId }
  | { type: 'moveIn'; player: PlayerId; from: TerritoryId; to: TerritoryId; count: number }
  | { type: 'eliminated'; player: PlayerId; by: PlayerId; cards: number }
  | { type: 'missionChanged'; player: PlayerId }
  | { type: 'fortify'; player: PlayerId; from: TerritoryId; to: TerritoryId; count: number }
  | { type: 'cardDrawn'; player: PlayerId; card: TerritoryId | null }
  | { type: 'lootDrawn'; player: PlayerId; options: LootKind[] }
  | { type: 'lootChosen'; player: PlayerId; kind: LootKind | null }
  | {
      type: 'lootPlayed';
      player: PlayerId;
      kind: LootKind;
      territory?: TerritoryId;
      from?: TerritoryId;
      to?: TerritoryId;
      count?: number;
      /** Tropas removidas por território (runas de ataque). */
      hits?: { territory: TerritoryId; lost: number }[];
      victim?: PlayerId;
      /** Cartas compradas/roubadas (só quem jogou vê). */
      cards?: string[];
    }
  | { type: 'lootDiscarded'; player: PlayerId; kind: LootKind }
  | { type: 'reaction'; player: PlayerId; used: boolean }
  | { type: 'effectExpired'; effect: Effect }
  | { type: 'power'; player: PlayerId; target: TerritoryId; owner: PlayerId }
  | { type: 'heal'; player: PlayerId; territory: TerritoryId }
  | { type: 'bossTimer'; boss: string; timer: number }
  | {
      type: 'bossRaid';
      boss: string;
      region: RegionId;
      raid: RaidDef['type'];
      losses: { territory: TerritoryId; lost: number }[];
      frozen: TerritoryId[];
      discards: { player: PlayerId; count: number }[];
    }
  | { type: 'bossRoll'; roll: number; spawn: boolean }
  | { type: 'bossRegionRoll'; rolls: number[]; regions: RegionId[]; chooser: PlayerId | null }
  | { type: 'bossSpawn'; boss: string; region: RegionId }
  | {
      type: 'bossCombat';
      player: PlayerId;
      from: TerritoryId;
      boss: string;
      attackRolls: number[];
      defenseRolls: number[];
      attackerLoss: number;
      bossLoss: number;
      hpLeft: number;
      notes: CombatNote[];
    }
  | { type: 'bossKilled'; boss: string; player: PlayerId }
  /** Recompensa por dano no boss: cartas de território a cada N de vida tirada. */
  | { type: 'bossDamageReward'; boss: string; player: PlayerId; damage: number; cards: number }
  /** Primeiro a dominar uma região: ganha loot (o tipo só aparece para o dono). */
  | { type: 'regionFirst'; region: RegionId; player: PlayerId; loot: LootKind | null }
  /** Cabeça a prêmio da rodada (null: empate, ninguém marcado). */
  | { type: 'bounty'; player: PlayerId | null }
  | { type: 'bountyClaimed'; player: PlayerId; target: PlayerId }
  | { type: 'skull'; player: PlayerId }
  /** Quem tinha a caveira perdeu um território: descartou carta e/ou loot. */
  | { type: 'skullPenalty'; player: PlayerId; by: PlayerId; card: boolean; loot: boolean }
  /** Rajada de harmonia: gastou toda a harmonia acumulada. */
  | { type: 'harmony'; player: PlayerId; spent: number; guard: boolean }
  | { type: 'missionComplete'; player: PlayerId }
  | { type: 'gameOver'; result: GameResult };
